// LocalStorage management utilities
import { User } from '@/data/mockData';

export interface StorageKeys {
  USERS: 'procurement_users';
  SKUS: 'procurement_skus';
  VENDORS: 'procurement_vendors';
  PURCHASE_ORDERS: 'procurement_pos';
  QUALITY_INSPECTIONS: 'procurement_inspections';
  INVOICES: 'procurement_invoices';
  GATE_ENTRIES: 'procurement_gate_entries';
  HANDOVER_LOGS: 'procurement_handover_logs';
  CURRENT_USER: 'procurement_current_user';
  APP_SETTINGS: 'procurement_settings';
  PRICE_HISTORY: 'procurement_price_history';
  VENDOR_INVOICES: 'procurement_vendor_invoices';
  POS_SENT_TO_VENDORS: 'procurement_pos_sent';
  DASHBOARD_STATS: 'procurement_dashboard_stats';
  APP_STATE: 'procurement_app_state';
  UI_STATE: 'procurement_ui_state';
  FILTERS: 'procurement_filters';
  BACKUP_DATA: 'procurement_backup_data';
}

export const STORAGE_KEYS: StorageKeys = {
  USERS: 'procurement_users',
  SKUS: 'procurement_skus',
  VENDORS: 'procurement_vendors',
  PURCHASE_ORDERS: 'procurement_pos',
  QUALITY_INSPECTIONS: 'procurement_inspections',
  INVOICES: 'procurement_invoices',
  GATE_ENTRIES: 'procurement_gate_entries',
  HANDOVER_LOGS: 'procurement_handover_logs',
  CURRENT_USER: 'procurement_current_user',
  APP_SETTINGS: 'procurement_settings',
  PRICE_HISTORY: 'procurement_price_history',
  VENDOR_INVOICES: 'procurement_vendor_invoices',
  POS_SENT_TO_VENDORS: 'procurement_pos_sent',
  DASHBOARD_STATS: 'procurement_dashboard_stats',
  APP_STATE: 'procurement_app_state',
  UI_STATE: 'procurement_ui_state',
  FILTERS: 'procurement_filters',
  BACKUP_DATA: 'procurement_backup_data'
};

export class StorageManager {
  /**
   * Save data to localStorage with retry mechanism
   */
  static save<T>(key: keyof StorageKeys, data: T): void {
    try {
      const serializedData = JSON.stringify(data);
      localStorage.setItem(STORAGE_KEYS[key], serializedData);
      console.log(`✅ Saved ${key} to localStorage (${serializedData.length} chars)`);
    } catch (error) {
      console.error(`❌ Error saving to localStorage (${key}):`, error);
      
      // Try to clear localStorage and retry once
      try {
        console.log(`🔄 Attempting to clear localStorage and retry for ${key}...`);
        localStorage.clear();
        localStorage.setItem(STORAGE_KEYS[key], JSON.stringify(data));
        console.log(`✅ Successfully saved ${key} after clearing localStorage`);
      } catch (retryError) {
        console.error(`❌ Failed to save ${key} even after clearing localStorage:`, retryError);
      }
    }
  }

  /**
   * Load data from localStorage with better error handling
   */
  static load<T>(key: keyof StorageKeys, defaultValue: T | null = null): T | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS[key]);
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log(`✅ Loaded ${key} from localStorage (${stored.length} chars)`);
        return parsed;
      } else {
        console.log(`ℹ️ No data found for ${key}, using default value`);
        return defaultValue;
      }
    } catch (error) {
      console.error(`❌ Error loading from localStorage (${key}):`, error);
      console.log(`🔄 Using default value for ${key}`);
      return defaultValue;
    }
  }

  /**
   * Remove data from localStorage
   */
  static remove(key: keyof StorageKeys): void {
    try {
      localStorage.removeItem(STORAGE_KEYS[key]);
    } catch (error) {
      console.error(`Error removing from localStorage (${key}):`, error);
    }
  }

  /**
   * Check if key exists in localStorage
   */
  static exists(key: keyof StorageKeys): boolean {
    return localStorage.getItem(STORAGE_KEYS[key]) !== null;
  }

  /**
   * Clear all procurement data from localStorage
   */
  static clearAll(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Get storage size for a specific key
   */
  static getSize(key: keyof StorageKeys): number {
    const data = localStorage.getItem(STORAGE_KEYS[key]);
    return data ? data.length : 0;
  }

  /**
   * Get total storage size for all procurement data
   */
  static getTotalSize(): number {
    return Object.values(STORAGE_KEYS).reduce((total, key) => {
      const data = localStorage.getItem(key);
      return total + (data ? data.length : 0);
    }, 0);
  }

  /**
   * Initialize localStorage with default data if empty
   */
  static initializeDefaults<T>(key: keyof StorageKeys, defaultData: T): void {
    if (!this.exists(key)) {
      this.save(key, defaultData);
    }
  }

  /**
   * Backup all data to a downloadable JSON file
   */
  static exportData(): string {
    const backup: Record<string, any> = {};
    Object.entries(STORAGE_KEYS).forEach(([keyName, storageKey]) => {
      const data = localStorage.getItem(storageKey);
      if (data) {
        backup[keyName] = JSON.parse(data);
      }
    });
    return JSON.stringify(backup, null, 2);
  }

  /**
   * Import data from a JSON string
   */
  static importData(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      Object.entries(data).forEach(([keyName, value]) => {
        const storageKey = STORAGE_KEYS[keyName as keyof StorageKeys];
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(value));
        }
      });
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }

  /**
   * Get storage usage statistics
   */
  static getStorageStats() {
    const stats: Record<string, { size: number; items?: number }> = {};
    
    Object.entries(STORAGE_KEYS).forEach(([keyName, storageKey]) => {
      const data = localStorage.getItem(storageKey);
      if (data) {
        try {
          const parsed = JSON.parse(data);
          stats[keyName] = {
            size: data.length,
            items: Array.isArray(parsed) ? parsed.length : 1
          };
        } catch {
          stats[keyName] = { size: data.length };
        }
      }
    });

    return {
      individual: stats,
      total: this.getTotalSize(),
      maxQuota: 5 * 1024 * 1024 // 5MB typical localStorage limit
    };
  }

  /**
   * Create a comprehensive backup of all application state
   */
  static createFullBackup(): string {
    const backup = {
      timestamp: new Date().toISOString(),
      version: '1.0.0',
      data: {} as Record<string, any>
    };

    // Backup all data
    Object.entries(STORAGE_KEYS).forEach(([keyName, storageKey]) => {
      const data = localStorage.getItem(storageKey);
      if (data) {
        try {
          backup.data[keyName] = JSON.parse(data);
        } catch (error) {
          console.warn(`Failed to backup ${keyName}:`, error);
        }
      }
    });

    return JSON.stringify(backup, null, 2);
  }

  /**
   * Restore application state from backup
   */
  static restoreFromBackup(backupJson: string): boolean {
    try {
      const backup = JSON.parse(backupJson);
      
      if (!backup.data || typeof backup.data !== 'object') {
        throw new Error('Invalid backup format');
      }

      // Restore all data
      Object.entries(backup.data).forEach(([keyName, data]) => {
        const storageKey = STORAGE_KEYS[keyName as keyof StorageKeys];
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(data));
        }
      });

      console.log('Successfully restored from backup:', backup.timestamp);
      return true;
    } catch (error) {
      console.error('Failed to restore from backup:', error);
      return false;
    }
  }

  /**
   * Auto-save current state (called periodically)
   */
  static autoSave(state: any): void {
    try {
      const stateBackup = {
        timestamp: new Date().toISOString(),
        state: state
      };
      this.save('APP_STATE', stateBackup);
    } catch (error) {
      console.warn('Auto-save failed:', error);
    }
  }

  /**
   * Restore last saved state
   */
  static restoreLastState(): any | null {
    try {
      const backup = this.load('APP_STATE');
      if (backup && backup.state) {
        console.log('Restoring last saved state from:', backup.timestamp);
        return backup.state;
      }
    } catch (error) {
      console.warn('Failed to restore last state:', error);
    }
    return null;
  }

  /**
   * Save UI state (filters, selected items, etc.)
   */
  static saveUIState(uiState: any): void {
    this.save('UI_STATE', {
      timestamp: new Date().toISOString(),
      state: uiState
    });
  }

  /**
   * Restore UI state
   */
  static restoreUIState(): any | null {
    try {
      const backup = this.load('UI_STATE');
      return backup?.state || null;
    } catch (error) {
      console.warn('Failed to restore UI state:', error);
      return null;
    }
  }

  /**
   * Save filters state
   */
  static saveFilters(filters: any): void {
    this.save('FILTERS', {
      timestamp: new Date().toISOString(),
      filters: filters
    });
  }

  /**
   * Restore filters state
   */
  static restoreFilters(): any | null {
    try {
      const backup = this.load('FILTERS');
      return backup?.filters || null;
    } catch (error) {
      console.warn('Failed to restore filters:', error);
      return null;
    }
  }

  /**
   * Check if localStorage is available and has space
   */
  static isStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get available storage space
   */
  static getAvailableSpace(): number {
    try {
      const test = '__space_test__';
      let size = 0;
      while (true) {
        try {
          localStorage.setItem(test, 'x'.repeat(size));
          size += 1000;
        } catch {
          break;
        }
      }
      localStorage.removeItem(test);
      return size;
    } catch {
      return 0;
    }
  }

  /**
   * Check localStorage health and available space
   */
  static checkHealth(): { isHealthy: boolean; availableSpace: number; usedSpace: number; error?: string } {
    try {
      const testKey = 'procurement_health_check';
      const testData = 'x'.repeat(1024); // 1KB test data
      
      // Test write
      localStorage.setItem(testKey, testData);
      
      // Test read
      const retrieved = localStorage.getItem(testKey);
      if (retrieved !== testData) {
        return { isHealthy: false, availableSpace: 0, usedSpace: 0, error: 'Data integrity check failed' };
      }
      
      // Clean up test data
      localStorage.removeItem(testKey);
      
      // Calculate space usage
      let usedSpace = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('procurement_')) {
          usedSpace += localStorage.getItem(key)?.length || 0;
        }
      }
      
      // Estimate available space (most browsers have 5-10MB limit)
      const estimatedLimit = 5 * 1024 * 1024; // 5MB
      const availableSpace = Math.max(0, estimatedLimit - usedSpace);
      
      return {
        isHealthy: true,
        availableSpace,
        usedSpace
      };
    } catch (error) {
      return {
        isHealthy: false,
        availableSpace: 0,
        usedSpace: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Validate data integrity for critical keys
   */
  static validateDataIntegrity(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    try {
      // Check critical data keys
      const criticalKeys: (keyof StorageKeys)[] = [
        'USERS', 'VENDORS', 'SKUS', 'PURCHASE_ORDERS', 'INVOICES'
      ];
      
      for (const key of criticalKeys) {
        const data = this.load(key, []);
        if (Array.isArray(data)) {
          // Check if data has expected structure
          if (data.length > 0 && typeof data[0] !== 'object') {
            errors.push(`${key} contains invalid data structure`);
          }
        } else if (data !== null) {
          errors.push(`${key} is not an array as expected`);
        }
      }
      
      return {
        isValid: errors.length === 0,
        errors
      };
    } catch (error) {
      errors.push(`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return { isValid: false, errors };
    }
  }

  /**
   * Force save with compression for large data
   */
  static forceSave<T>(key: keyof StorageKeys, data: T): boolean {
    try {
      const serializedData = JSON.stringify(data);
      
      // If data is too large, try to compress or split
      if (serializedData.length > 1024 * 1024) { // 1MB
        console.warn(`⚠️ Large data detected for ${key} (${serializedData.length} chars), attempting to save...`);
      }
      
      localStorage.setItem(STORAGE_KEYS[key], serializedData);
      console.log(`✅ Force saved ${key} (${serializedData.length} chars)`);
      return true;
    } catch (error) {
      console.error(`❌ Force save failed for ${key}:`, error);
      return false;
    }
  }
}

// Utility functions for common operations
export const saveCurrentUser = (user: any) => {
  StorageManager.save('CURRENT_USER', user);
};

export const getCurrentUser = (): User | null => {
  return StorageManager.load('CURRENT_USER');
};

export const clearCurrentUser = () => {
  StorageManager.remove('CURRENT_USER');
};

export const isLoggedIn = (): boolean => {
  return getCurrentUser() !== null;
};

// Settings management
export interface AppSettings {
  theme: 'light' | 'dark' | 'system';
  language: string;
  dateFormat: string;
  currency: string;
  itemsPerPage: number;
  autoSave: boolean;
  notifications: {
    email: boolean;
    push: boolean;
    sound: boolean;
  };
  dashboard: {
    refreshInterval: number;
    defaultView: string;
    widgets: string[];
  };
}

export const DEFAULT_SETTINGS: AppSettings = {
  theme: 'system',
  language: 'en',
  dateFormat: 'MM/dd/yyyy',
  currency: 'USD',
  itemsPerPage: 10,
  autoSave: true,
  notifications: {
    email: true,
    push: true,
    sound: false
  },
  dashboard: {
    refreshInterval: 30000, // 30 seconds
    defaultView: 'overview',
    widgets: ['stats', 'recent-orders', 'pending-actions']
  }
};

export const getAppSettings = (): AppSettings => {
  return StorageManager.load('APP_SETTINGS', DEFAULT_SETTINGS) || DEFAULT_SETTINGS;
};

export const saveAppSettings = (settings: AppSettings) => {
  StorageManager.save('APP_SETTINGS', settings);
};

export const resetAppSettings = () => {
  StorageManager.save('APP_SETTINGS', DEFAULT_SETTINGS);
};
